import { Injectable } from '@angular/core';
import { ArticleConnectionService } from '@3d-scene/model3d/article-connection.service';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ArticleUtils } from '@article/util/article-utils';
import { CarrierBlockManager } from '@project/components/article/manager/carrier-block.manager';
import { CarrierBlockUtils } from '@project/components/article/util/carrier-block-utils';
import { TerminalBlockManager } from '@project/manager/terminal-block.manager';
import { ProjectModel } from '@project/model/project.model';
import { ArrayUtils } from '@shared/util/array-utils';
import { ApplicationRefreshService } from '@services/application-refresh.service';

@Injectable({providedIn: 'root'})
export class BmkService {

  constructor(
    private articleConnectionService: ArticleConnectionService,
    private terminalBlockManager: TerminalBlockManager,
    private applicationRefreshService: ApplicationRefreshService,
    private projectModel: ProjectModel,
    private carrierBlockManager: CarrierBlockManager,
    private containerService: ContainerService,
  ) {
  }

  /** @returns whether or not the given articlecontainers are eligible for being assigned a new bmk group
   * (are either all neighbors or rails) */
  public isEditBmkGroupAvailableForArticles(containers: ArticleContainer[]) {
    const nonMountingRailArticles = containers.filter((container) => !ArticleUtils.isMountingRail(container.article));
    return nonMountingRailArticles.length == 0 || this.areAllArticlesNeighbours(nonMountingRailArticles);
  }

  /**
   * sets the bmk group for the given articles if they are all neighbors
   * @param articleContainers the containers to assign a bmk to
   * @param bmkName the bmk name (or device tag)
   */
  public setBmkGroup(articleContainers: ArticleContainer[], bmkName: string): void {
    const containersOnRail = articleContainers
      .filter((container) => this.articleConnectionService.isArticleConnectedToMountingRail(container));

    if (containersOnRail.length > 0 && !this.areAllArticlesNeighbours(containersOnRail)) {
      throw Error('createBmkGroup called with articles which are not neighbors')
    }

    const rails = articleContainers
      .filter((container) => ArticleUtils.isMountingRail(container.article));

    const containersOnRailsParts = containersOnRail.flatMap((container) => this.containerService.getCompositePartArticles(container));

    if(bmkName.trim().length > 0) {
      this.setBmkStructure([...containersOnRail, ...containersOnRailsParts, ...rails], bmkName);
      this.setCarrierBlock([...containersOnRail, ...containersOnRailsParts], bmkName);
    } else {
      this.removeBmkStructure([...containersOnRail, ...containersOnRailsParts, ...rails]);
      this.carrierBlockManager.removeArticleContainersFromBlock([...containersOnRail, ...containersOnRailsParts]);
    }

    this.carrierBlockManager.updateCarrierBlocksInProject(this.projectModel.currentProject);
    this.projectModel.notifyCurrentProjectChanged();
    this.applicationRefreshService.notifyProjectChange();
  }

  private setBmkStructure(containersToSet: ArticleContainer[], bmkName: string) {
    for (const container of containersToSet) {
      ArticleUsageUtils.setArticleUsageInfoPropertyStringValue(container.articleUsage, ArticleUsageInfoPropertyDef.CAE_ITEM_STRUCTURE, bmkName)
    }
  }

  private removeBmkStructure(containersToSet: ArticleContainer[]) {
    for (const container of containersToSet) {
      ArticleUsageUtils.removeArticleUsageInfoProperty(container.articleUsage, ArticleUsageInfoPropertyDef.CAE_ITEM_STRUCTURE)
    }
  }

  private setCarrierBlock(containersToSet: ArticleContainer[], bmkName: string) {
    this.carrierBlockManager.removeArticleContainersFromBlock(containersToSet);

    // carrierBlockName is the bmkName split at the first occurring '-' or just empty
    let carrierBlockName = bmkName.split(/-(.*)/s)[1] ?? '';
    if (carrierBlockName != '') {
      carrierBlockName = '-' + carrierBlockName;
    }

    const existingBlock = this.carrierBlockManager.findCarrierBlockByBmkName(bmkName);

    for (const container of containersToSet) {
      CarrierBlockUtils.setCarrierBlockGuidAndBmk(container.articleUsage, existingBlock?.guid ?? bmkName, bmkName);
    }

    const guids = containersToSet.map((container) => container.articleGUID);
    this.carrierBlockManager.addArticleGuidsToCarrierBlock(guids, bmkName, carrierBlockName);
  }

  public readBmkNamesFromArticles(articleContainers: ArticleContainer[]): string[] {
    let bmkNames: string[] = [];
    for (const container of articleContainers) {
      const articleBmK = ArticleUsageUtils.getArticleUsageInfoPropertyStringValue(container.articleUsage, ArticleUsageInfoPropertyDef.CAE_ITEM_STRUCTURE);
      if (!bmkNames.includes(articleBmK)) {
        bmkNames.push(articleBmK);
      }
    }
    return bmkNames;
  }

  public areAllArticlesNeighbours(containers: ArticleContainer[]): boolean {
    if (ArrayUtils.isEmpty(containers)) return false;

    const containersOnRail = containers
      .filter((container) => this.articleConnectionService.isArticleConnectedToMountingRail(container));

    const sortedSelectedGuids = containersOnRail
      .sort(((a, b) => a.position.x - b.position.x))
      .map((container) => container.articleGUID);

    const neighborsOfFirstTerminal = this.terminalBlockManager.getArticleContainersInTerminalBlock(containersOnRail[0], []);

    const allSortedNeighboringGuids = neighborsOfFirstTerminal
      .sort(((a, b) => a.position.x - b.position.x))
      .map((container) => container.articleGUID);

    return ArrayUtils.includesContiguous(allSortedNeighboringGuids, sortedSelectedGuids);
  }
}
